<script setup lang="ts">
import { Search } from 'lucide-vue-next';
import { ref } from 'vue';

import type { CampaignFilters, FilterSelectOption } from '../types';

defineProps<{
  incentiveOptions: FilterSelectOption[];
}>();

const emit = defineEmits<{
  apply: [filters: CampaignFilters];
  clear: [];
}>();

const searchInput = ref('');
const statusInput = ref<CampaignFilters['status']>('');
const incentiveInput = ref('');

function apply() {
  emit('apply', {
    incentiveId: incentiveInput.value,
    search: searchInput.value,
    status: statusInput.value,
  });
}

function clear() {
  searchInput.value = '';
  statusInput.value = '';
  incentiveInput.value = '';
  emit('clear');
}
</script>

<template>
  <div class="col-12">
    <div class="filters-bar">
      <div class="filter-group">
        <label class="filter-label" for="cmp-search">Buscar</label>
        <input
          id="cmp-search"
          v-model="searchInput"
          type="text"
          class="filter-input"
          placeholder="Nome ou código (ex.: CAM-ABC12345)…"
          @keydown.enter="apply"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label" for="cmp-status">Status</label>
        <select id="cmp-status" v-model="statusInput" class="filter-input">
          <option value="">Todos</option>
          <option value="active">Ativa</option>
          <option value="draft">Rascunho</option>
          <option value="processing">Processando</option>
          <option value="scheduled">Agendada</option>
          <option value="paused">Pausada</option>
          <option value="finished">Encerrada</option>
          <option value="canceled">Cancelada</option>
        </select>
      </div>

      <div class="filter-group">
        <label class="filter-label" for="cmp-incentive">Tipo de incentivo</label>
        <select id="cmp-incentive" v-model="incentiveInput" class="filter-input">
          <option value="">Todos</option>
          <option
            v-for="option in incentiveOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="filter-actions">
        <button type="button" class="btn-sol btn-secondary" @click="clear">
          Limpar
        </button>
        <button type="button" class="btn-sol btn-primary" @click="apply">
          <Search class="icon icon-sm" />
          Buscar
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.filters-bar {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  margin-bottom: 4px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr auto;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;

    .filter-actions {
      grid-column: 1;
    }
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 0;
}

.filter-input {
  padding: 8px 10px;
  border: 1px solid #e4e6eb;
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
  color: #222222;
  width: 100%;
  height: auto;
  line-height: 1.5;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #00b569;
  }
}

.filter-actions {
  display: flex;
  gap: 8px;
  white-space: nowrap;
  align-items: flex-end;
}

.btn-sol {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;

  &.btn-primary {
    background: #00b569;
    color: #fff;

    &:hover { background: #009957; }
  }

  &.btn-secondary {
    background: #fff;
    color: #222;
    border-color: #e4e6eb;

    &:hover { background: #f5f6f8; }
  }
}
</style>
